// One fetch wrapper for the whole panel.
//
// The server's error envelope is always { error: { code, message, details } },
// so every failure surfaces as an ApiError carrying that code — screens branch
// on the code and only ever display the message, same contract as the app.

const ACCESS_KEY = 'pathology.admin.accessToken';
const REFRESH_KEY = 'pathology.admin.refreshToken';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set on the retry after a refresh, so a loop is impossible. */
  retried?: boolean;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError(response.status, 'INTERNAL', 'Server sent a non-JSON response');
  }
}

/**
 * Access tokens last 15 minutes, so a long editing session will hit one
 * expiry. A single transparent refresh keeps that invisible; a second failure
 * means the refresh token is dead too and the panel signs out.
 */
async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  const response = await fetch('/v1/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    tokens.clear();
    return false;
  }

  const body = (await response.json()) as { tokens: { accessToken: string; refreshToken: string } };
  tokens.set(body.tokens.accessToken, body.tokens.refreshToken);

  return true;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (tokens.access) headers.authorization = `Bearer ${tokens.access}`;

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401 && !options.retried && (await refreshTokens())) {
    return api<T>(path, { ...options, retried: true });
  }

  const body = await parse(response);

  if (!response.ok) {
    const envelope = (body as { error?: { code?: string; message?: string; details?: unknown } })?.error;
    throw new ApiError(
      response.status,
      envelope?.code ?? 'INTERNAL',
      envelope?.message ?? response.statusText,
      envelope?.details,
    );
  }

  return body as T;
}

/** Turns an ApiError's validation details into one readable line. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const details = error.details as { path?: string; message?: string }[] | undefined;

    if (Array.isArray(details) && details.length > 0) {
      return `${error.message}: ${details.map((d) => `${d.path} ${d.message}`).join(', ')}`;
    }

    return error.message;
  }

  return error instanceof Error ? error.message : 'Something went wrong';
}
